import type { Report } from '../types';
import { loadProjectById, saveProjectToCloud } from './cloudService';
import { isProjectSaveConflict } from './projectVersionAnchor';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * FRISCH LADEN, ÄNDERN, MIT DEM ANKER DIESES LADENS SPEICHERN
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Für Änderungen, die NICHT aus einem geöffneten Projekt kommen (Liste,
 * Wartung): das Projekt voll laden (`loadProjectById`), die Änderung auf
 * DIESEN Stand anwenden und mit genau dessen `updated_at` als Anker und
 * `onConflict: 'throw'` speichern.
 *
 * Warum nicht die Vorgabe (`'salvage'`): die Bergung übernimmt bei einem
 * Ankerkonflikt nur die fremden Protokollzeilen und schreibt alles andere aus
 * dem eigenen, inzwischen überholten Stand über den fremden
 * (cloudService.ts, „DIE BERGUNG"). Für einen Stand, der eben erst geladen
 * wurde, ist das falsch — hier wird stattdessen frisch geladen und die
 * Änderung NEU berechnet (`aendern` läuft je Versuch). Der Anker wird geprüft,
 * bevor irgendetwas geschrieben wird; ein Konflikt hinterlässt nichts.
 *
 * Bleibt der Konflikt über alle Versuche, wirft der letzte
 * `ProjectSaveConflictError` — der Aufrufer entscheidet (melden,
 * überspringen).
 */

/** Wie oft bei einem Ankerkonflikt frisch geladen und neu angesetzt wird. */
export const FRISCH_SPEICHERN_VERSUCHE = 3;

/** Was `loadProjectById` liefert, soweit es hier gebraucht wird. */
export interface FrischGeladenesProjekt {
    updated_at?: string | null;
    next_scheduled_date?: string | null;
    data?: Report | null;
}

export interface FrischSpeichernDeps {
    loadProjectById: (id: string) => Promise<FrischGeladenesProjekt | null>;
    saveProjectToCloud: typeof saveProjectToCloud;
}

// Erst beim Aufruf gebaut, nicht beim Laden des Moduls: Tests, die cloudService
// mit einer Teilmenge ersetzen (AdminGlobalSettings.test), sollen dieses Modul
// importieren koennen, ohne dass der fehlende Export sofort gelesen wird.
const standardDeps = (): FrischSpeichernDeps => ({ loadProjectById, saveProjectToCloud });

/** Der frisch geladene Stand, auf den `aendern` rechnet. */
export interface FrischerStand {
    report: Report;
    /** Spalte `next_scheduled_date` — die jüngere Quelle des Folgetermins. */
    naechsterTermin: string | null;
}

export class ProjektNichtLadbarError extends Error {
    readonly name = 'ProjektNichtLadbarError';
    constructor(readonly projectId: string) {
        super('Das Projekt ließ sich nicht vollständig laden — es wurde NICHTS gespeichert.');
    }
}

/**
 * Was am Ende gilt: der geschriebene Bericht — oder, wenn `aendern` nichts
 * zu tun hatte, der eben geladene. In BEIDEN Fällen der aktuelle Stand, mit
 * dem ein Aufrufer seine Anzeige abgleichen kann (eine Liste, die noch einen
 * inzwischen entfernten Turnus zeigt, soll ihn danach nicht mehr zeigen).
 */
export interface FrischGespeichert extends FrischerStand {
    /** false: `aendern` lieferte null, es wurde nichts gespeichert. */
    geschrieben: boolean;
}

/**
 * Lädt `projectId` voll, wendet `aendern` an und speichert mit Anker.
 * `aendern` liefert `null`, wenn es nichts zu tun gibt — dann wird nicht
 * gespeichert (`geschrieben: false`, `report` = der geladene Stand).
 */
export const speichereFrischGeladen = async (
    projectId: string,
    aendern: (stand: FrischerStand) => Report | null,
    deps: FrischSpeichernDeps = standardDeps(),
    versuche: number = FRISCH_SPEICHERN_VERSUCHE,
): Promise<FrischGespeichert> => {
    for (let versuch = 1; ; versuch++) {
        const voll = await deps.loadProjectById(projectId);
        if (!voll?.data?.metadata) throw new ProjektNichtLadbarError(projectId);
        const naechsterTermin = voll.next_scheduled_date ?? null;
        const neu = aendern({ report: voll.data, naechsterTermin });
        if (!neu) return { geschrieben: false, report: voll.data, naechsterTermin };
        try {
            await deps.saveProjectToCloud(neu, projectId, undefined, undefined, undefined, {
                expectedUpdatedAt: voll.updated_at ?? null,
                onConflict: 'throw',
            });
            return { geschrieben: true, report: neu, naechsterTermin: neu.metadata.intervalConfig?.nextScheduledDate ?? naechsterTermin };
        } catch (e) {
            if (!isProjectSaveConflict(e) || versuch >= versuche) throw e;
        }
    }
};
